import { useState, useEffect, ReactElement, ReactNode } from 'react';
import 'react-day-picker/dist/style.css';
import { QueryClient, QueryClientProvider, QueryCache } from 'react-query';
import 'reactjs-popup/dist/index.css';
import type { NextPage } from 'next';
import { DefaultSeo } from 'next-seo';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  BREAKPOINTS_WIDTHS,
  COOKIES_CONFIG,
  RouterProvider,
} from '@belong/common';
import { Footer } from 'src/components/footer/footer';
import '@belong/ui/index.css';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import '@reach/accordion/styles.css';
import '@reach/dialog/styles.css';
import '@reach/listbox/styles.css';
import '@reach/slider/styles.css';
import '@reach/tabs/styles.css';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import 'lazysizes/plugins/parent-fit/ls.parent-fit';
import { parseCookies } from 'nookies';
import { Header } from 'src/components/header/header';
import { NProgress } from 'src/components/nprogress/nprogress';
import { SystemNoticeToast } from 'src/components/system-notice-toast/system-notice-toast';
import { ClientInit } from 'src/features/app/client-init';
import { PrismicPreview } from 'src/features/cms/components/prismic-preview';
import { ConsentManager } from 'src/features/consent/components/consent-manager/consent-manager';
import { CustomerioAnalyticsProvider } from 'src/features/document/customerio';
import { DataDog } from 'src/features/document/data-dog';
import { FEATURE_FLAGS_CONFIG } from 'src/features/feature-flags/constants';
import { SupportChat } from 'src/features/support-chat/components/support-chat/support-chat';
import { setSupportChatLauncherVisible } from 'src/features/support-chat/utils';
import { ModalManager } from 'src/modals/modal-manager/modal-manager';
import { useCreateStore, Provider } from 'src/store';
import { isProd } from 'src/utils/is-prod';
import { pageview } from 'src/utils/tracking';
import '../styles/globals.css';

console.log('Build Number:', process.env.NEXT_PUBLIC_BUILD_NUMBER);

console.log('octane', process.env.NEXT_PUBLIC_OCTANE_URL);

export function getDefaultLayout(page: ReactNode): ReactNode {
  const cookies = parseCookies();
  const comeFromMobileApp = Boolean(
    cookies[COOKIES_CONFIG.MOBILE_APP.name] === 'true',
  );

  return (
    <div className="flex min-h-full flex-col">
      {!comeFromMobileApp && <Header />}
      <main className="flex-1">{page}</main>
      {!comeFromMobileApp && <Footer />}
    </div>
  );
}

function CustomApp({ Component, pageProps }: CustomAppProps) {
  const router = useRouter();

  const createStore = useCreateStore(pageProps.initialZustandState);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: async (error) => {
            if (typeof window === 'undefined') {
              return;
            }

            // A TypeError here means the request never completed (network down,
            // CORS, DNS) — which is also what happens when the backend is in
            // maintenance. Confirm against the oracle before redirecting.
            if (error instanceof TypeError) {
              try {
                const res = await fetch('/api/maintenance-windows');

                if (!res.ok) {
                  window.location.replace('/maintenance');

                  return;
                }

                const windows: unknown[] = await res.json();

                if (windows.length > 0) {
                  window.location.replace('/maintenance');
                }
              } catch {
                // Fail open: if the oracle check itself can't complete we don't
                // know there's an outage, so leave the user where they are
                // rather than stranding a healthy session on /maintenance.
                // React Query will retry the original query.
              }
            }
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 10000,
            retry: isProd,
            refetchOnWindowFocus: isProd,
          },
        },
      }),
  );

  useEffect(() => {
    if (router.pathname === '/member-view') return;
    NProgress.configure({ trickleSpeed: 200 });

    function routeChangeStart(_: string, { shallow }: { shallow: boolean }) {
      window.mixpanel?.register({
        'Previous URL': router.asPath,
      });

      if (router.isReady && !shallow) {
        NProgress.start();

        setSupportChatLauncherVisible(false);
      }
    }

    function routeChangeComplete(
      url: string,
      { shallow }: { shallow: boolean },
    ) {
      if (router.isReady) {
        pageview(url);

        if (!shallow) {
          NProgress.done();

          // Show the chat launcher on accounts pages, except on mobile where it covers the bottom menu
          if (router.asPath.includes('/accounts')) {
            setSupportChatLauncherVisible(
              window.innerWidth >= BREAKPOINTS_WIDTHS.MD,
            );
          }
        }
      }
    }

    function routeChangeError(_: string, { shallow }: { shallow: boolean }) {
      if (router.isReady && !shallow) {
        NProgress.done();
      }
    }

    router.events.on('routeChangeStart', routeChangeStart);
    router.events.on('routeChangeComplete', routeChangeComplete);
    router.events.on('routeChangeError', routeChangeError);

    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
      router.events.off('routeChangeComplete', routeChangeComplete);
      router.events.off('routeChangeError', routeChangeError);

      window.Intercom?.('hide');
      setSupportChatLauncherVisible(false);
    };
  }, [router.isReady]);

  if (
    router.pathname === '/maintenance' ||
    router.pathname === '/member-view'
  ) {
    return <Component {...pageProps} />;
  }

  const getLayout = Component.getLayout || getDefaultLayout;

  return (
    <ConsentManager>
      <SplitFactoryProvider config={FEATURE_FLAGS_CONFIG}>
        <QueryClientProvider client={queryClient}>
          <CustomerioAnalyticsProvider>
            <Provider createStore={createStore}>
              <TooltipProvider delayDuration={50} skipDelayDuration={150}>
                <RouterProvider link={Link} router={router}>
                  <PrismicPreview repositoryName="belongtest">
                    <SupportChat />
                    <ClientInit>
                      {getLayout(
                        <>
                          {/* https://nextjs.org/docs/messages/no-document-viewport-meta */}
                          <Head>
                            <meta
                              name="viewport"
                              content="width=device-width, initial-scale=1, viewport-fit=cover"
                            />
                          </Head>

                          {/* Twitter will read the og:title, og:image and og:description tags for their card,
                          this is why next-seo omits twitter:title, twitter:image and twitter:description so not to duplicate.
                          */}
                          <DefaultSeo
                            titleTemplate="%s"
                            defaultTitle="Renting People Love | For Homeowners and Residents"
                            description="Renting is the new American lifestyle. We didn't create it. But we're making it sing."
                            canonical="https://belonghome.com/"
                            facebook={{
                              appId: '1906883602682636',
                            }}
                            twitter={{
                              site: '@joinbelong',
                              cardType: 'summary_large_image',
                            }}
                            openGraph={{
                              type: 'website',
                              url: 'https://belonghome.com/',
                              site_name: 'Belong',
                              title:
                                'Renting People Love | For Homeowners and Residents',
                              description:
                                "Renting is the new American lifestyle. We didn't create it. But we're making it sing.",
                              images: [
                                {
                                  url: 'https://s3-us-west-2.amazonaws.com/belong-web-assets/og-image.png',
                                  alt: 'The magical world of Belong.',
                                  width: 1200,
                                  height: 630,
                                },
                              ],
                            }}
                          />

                          <Component {...pageProps} />

                          <ModalManager />
                          <SystemNoticeToast />
                          <DataDog />
                        </>,
                        {
                          ...pageProps,
                        },
                      )}
                    </ClientInit>
                  </PrismicPreview>
                </RouterProvider>
              </TooltipProvider>
            </Provider>
          </CustomerioAnalyticsProvider>
        </QueryClientProvider>
      </SplitFactoryProvider>
    </ConsentManager>
  );
}

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement, pageProps?: unknown) => ReactNode;
};

type CustomAppProps = AppProps & {
  Component: NextPageWithLayout;
  pageProps: unknown;
};

export default CustomApp;
